import { useState } from "react"
import { Form, Formik } from "formik"
import { Button, Modal, Table } from "react-bootstrap"
import * as yup from 'yup'
import { useAppDispatch } from "../../../redux/store"
import { showError, showSuccess } from "../../../redux/counter/counterSlice"
import { useEditBookingMutation } from "../../../services/bClientApi"
import canAccess from "../../../services/accessService/accessService"
import { currencyFormatter } from "../../../Helpers/Parsers/currencyFormatter"
import { BookingFeeEntry, PropertyBookingEntry } from "../../../Types/bookingTypes"
import { SelectWithOtherField } from "../../FormFields/SelectWithOtherInput/SelectWithOtherField"
import { TextInputField } from "../../FormFields/TextInput/TextInputField"
import { bookingFeeSourceFieldValues, bookingFeeTypeFieldValues } from "../../Forms/BookingForm/BookingFormConstants"

interface BookingFeesProps {
  bookingData: PropertyBookingEntry
}

// index -1 means a new fee is being added
type EditTarget = { index: number }

const feeLabel = (options: { value: string, text: string }[], value: string) => (
  options.find((option) => option.value === value)?.text || value
)

const validationSchema = yup.object().shape({
  feeType: yup.string().required('Fee type is required'),
  feeSource: yup.string().required('Fee source is required'),
  feeAmount: yup.number()
    .typeError('Fee amount must be a number')
    .required('Fee amount is required'),
})

export default function BookingFees({ bookingData }: BookingFeesProps) {
  const dispatch = useAppDispatch()
  const [editBooking, editState] = useEditBookingMutation()
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null)

  const canEdit = canAccess('routes', 'edit-booking', { payload: bookingData })
  const fees: BookingFeeEntry[] = bookingData.fees || []
  const feeTotal = fees.reduce((total, fee) => total + (Number(fee.feeAmount) || 0), 0)

  const isNew = editTarget?.index === -1
  const currentFee = editTarget && !isNew ? fees[editTarget.index] : null

  const saveFees = async (updatedFees: BookingFeeEntry[], successMessage: string) => {
    if (!bookingData._id) {
      return false
    }
    try {
      await editBooking({
        bookingId: bookingData._id,
        bookingInfo: { fees: updatedFees },
      }).unwrap()
      dispatch(showSuccess(successMessage))
      return true
    } catch (error: any) {
      dispatch(showError(error?.data?.error || 'Unable to update fees'))
      return false
    }
  }

  const handleSave = async (values: { feeType: string, feeSource: string, feeAmount: string | number }) => {
    if (!editTarget) {
      return
    }
    const updatedFee = {
      feeType: values.feeType,
      feeSource: values.feeSource,
      feeAmount: Number(values.feeAmount),
    } as BookingFeeEntry
    const updatedFees = [...fees]
    if (isNew) {
      updatedFees.push(updatedFee)
    } else {
      updatedFees[editTarget.index] = updatedFee
    }
    if (await saveFees(updatedFees, isNew ? 'Fee added' : 'Fee updated')) {
      setEditTarget(null)
    }
  }

  const handleRemove = async (index: number) => {
    await saveFees(fees.filter((_, feeIndex) => feeIndex !== index), 'Fee removed')
  }

  return (
    <div className="mb-4">
      <h3>Fees</h3>

      {fees.length === 0 && (
        <p className="text-muted">No fees</p>
      )}

      {fees.length > 0 && (
        <Table size="sm" responsive>
          <thead>
            <tr>
              <th>Type</th>
              <th>Source</th>
              <th>Amount</th>
              {canEdit && <th />}
            </tr>
          </thead>
          <tbody>
            {fees.map((fee, index) => (
              <tr key={`booking-fee-${index}`}>
                <td>{feeLabel(bookingFeeTypeFieldValues, fee.feeType)}</td>
                <td>{feeLabel(bookingFeeSourceFieldValues, fee.feeSource)}</td>
                <td>{currencyFormatter(fee.feeAmount)}</td>
                {canEdit && (
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="py-0 me-1"
                      title="Edit fee"
                      disabled={editState.isLoading}
                      onClick={() => setEditTarget({ index })}
                    >
                      ✏️
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      className="py-0"
                      title="Remove fee"
                      disabled={editState.isLoading}
                      onClick={() => handleRemove(index)}
                    >
                      🗑️
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={2}>Total</th>
              <th>{currencyFormatter(feeTotal)}</th>
              {canEdit && <th />}
            </tr>
          </tfoot>
        </Table>
      )}

      {canEdit && (
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => setEditTarget({ index: -1 })}
        >
          + Add Fee
        </Button>
      )}

      <Modal show={Boolean(editTarget)} onHide={() => setEditTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{isNew ? 'Add Fee' : 'Edit Fee'}</Modal.Title>
        </Modal.Header>
        <Formik
          key={editTarget ? `fee-${editTarget.index}` : 'closed'}
          initialValues={{
            feeType: currentFee?.feeType || '',
            feeSource: currentFee?.feeSource || '',
            feeAmount: currentFee?.feeAmount ?? '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSave}
        >
          {({ isSubmitting }) => (
            <Form>
              <Modal.Body>
                <SelectWithOtherField
                  fieldLabel="Fee Type"
                  placeholder="- Select Fee Type -"
                  fieldName="feeType"
                  options={bookingFeeTypeFieldValues}
                  required
                  disabled={editState.isLoading} />
                <SelectWithOtherField
                  fieldLabel="Fee Source"
                  placeholder="- Select Fee Source -"
                  fieldName="feeSource"
                  options={bookingFeeSourceFieldValues}
                  required
                  disabled={editState.isLoading} />
                <TextInputField
                  fieldLabel="Fee Amount"
                  placeholder="Fee Amount"
                  fieldName="feeAmount"
                  fieldType="number"
                  required
                  disabled={editState.isLoading} />
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" type="button" onClick={() => setEditTarget(null)} disabled={editState.isLoading}>
                  Cancel
                </Button>
                <Button type="submit" disabled={editState.isLoading || isSubmitting}>
                  {editState.isLoading ? 'Saving...' : 'Save'}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  )
}
